import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, Observer, of, tap } from 'rxjs';
import { catchError, defaultIfEmpty, map, mergeMap, switchMap } from 'rxjs/operators';

import { ORGANIZATION_ID } from '../../../modules/organizations/services/org-overview.service';
import { constants } from '../../../modules/patient-overview/shared/constants';
import { IEncounterRequest } from '../../../modules/provider-overview/services/patient-encounter.service';
import { OrganizationTypes } from '../../../shared/enums/organization';
import { Branch } from '../../classes/branch.class';
import { ITaskList } from '../../interfaces/TaskListItem.interface';
import { Appointment } from '../../models/appointment.model';
import { CareplanTaskModel } from '../../models/careplan-task-model';
import { IEncounterHistoryListResource } from '../../models/encounter/encounter.model';
import { IPatientGroup, IPatientGroups } from '../../models/patient-groups.model';
import { LocationModel, Provider, ProviderResponse, ProviderUpdatedResponse } from '../../models/provider.model';
import { UserSession } from '../../models/user-session.model';
import { DateUtility } from '../../utils/date-utility';
import { LinkUtil } from '../../utils/link-util/link-util.service';
import { DataBrokerService } from '../data-broker/data-broker.service';
import { ImageService } from '../image/image.service';
import { NetworkService } from '../network/network.service';
import { OperationUserSessionService } from '../user-session/operation-user-session.service';
import { UserSessionService } from '../user-session/user-session.service';

@Injectable({ providedIn: 'root' })
export class ProviderService {
    apiUrl = '/api/performers';
    private providerSubject = new BehaviorSubject<Provider>(undefined);
    private selectedOrganization: BehaviorSubject<Branch> = new BehaviorSubject<Branch>(null);
    public selectedOrganizationId: string;
    public providerSSOdUrl = '/curapatient/provider/dashboard?student_id=';

    constructor(
        private network: NetworkService,
        private linkUtil: LinkUtil,
        private imageService: ImageService,
        private data: DataBrokerService,
        private userSession: UserSessionService,
        private operationUserSessionService: OperationUserSessionService,
        private router: Router,
    ) {}

    public getAvailableLocations(organizationId: string): Observable<LocationModel[]> {
        const link = `/api/organizations/${organizationId}/locations`;

        return this.network.fetchResource(link);
    }

    public getProviderNotifications(link) {
        return this.network
            .fetchResource(link)
            .pipe(
                mergeMap(({ content, links }) => {
                    const notifications: any[] = content.map((c) => {
                        const actorImgLink = this.linkUtil.parseLink(c.links, 'actorImg');

                        if (actorImgLink) {
                            return this.imageService.getImageNew(actorImgLink, 100).pipe(
                                map((actorImg) => ({ ...c, actorImg })),
                                catchError(() => of({ ...c, actorImg: '' })),
                            );
                        }

                        return of(c);
                    });

                    if (content.length) {
                        return forkJoin(notifications).pipe(map((iContent: any[]) => ({ content: iContent, links })));
                    }

                    return of({ content, links });
                }),
                defaultIfEmpty({ content: [], links: null }),
            )
            .pipe(
                mergeMap(({ content, links }) => {
                    const notifications = content.map((c) => {
                        const relatedAccountImgLink = this.linkUtil.parseLink(c.links, 'relatedAccountImg');

                        if (relatedAccountImgLink) {
                            return this.imageService.getImageNew(relatedAccountImgLink, 100).pipe(
                                map((relatedAccountImg) => ({ ...c, relatedAccountImg })),
                                catchError(() => of({ ...c, relatedAccountImg: '' })),
                            );
                        }

                        return of(c);
                    });

                    if (content.length) {
                        return forkJoin(notifications).pipe(map((iContent: any) => ({ content: iContent, links })));
                    }

                    return of({ content, links });
                }),
                defaultIfEmpty({ content: [], links: null }),
            );
    }

    public replyToRequest({ activity, ifAccept }) {
        const replyToRequestUrl = this.linkUtil.parseLink(activity.links, 'replyToRequest');

        this.network.putResource<any>(replyToRequestUrl, { ifAccept, activityId: activity.id }).subscribe(() => {
            this.data.publish(activity, 'reply-to-appointment-request');
        });
    }

    public getProviders(serviceTypeId) {
        /*
         * const providerLink = this.linkUtil.parseLink(links, 'serviceProviders');
         * this.getUrl(this.session.sessionAccount.links, 'serviceProviders'); // TODO: Fetch url from session
         */
        const serviceLink = '/api/performers/serviceProviders';
        const serviceUrl = `${serviceLink}?serviceTypeId=${serviceTypeId}`;

        return this.network.fetchResource(serviceUrl).pipe(
            mergeMap((response: any[]) => {
                const providers = response.map((provider) => {
                    const providerImageLink = this.linkUtil.parseLink(provider.links, 'providerImage');

                    return this.imageService.getImageNew(providerImageLink, 100).pipe(
                        map((providerImage) => ({ ...provider, providerImage })),
                        catchError(() => of({ ...provider, providerImage: '' })),
                    );
                });

                return forkJoin(providers);
            }),
            defaultIfEmpty([]),
        );
    }

    public getSelectedProvider(link?: string): Observable<Provider> {
        const currentProviderLink = link || localStorage.getItem('current-provider');

        if (this.providerSubject.value === undefined || currentProviderLink) {
            return this.getProviderByLink(currentProviderLink).pipe(
                tap((data) => {
                    this.providerSubject.next(data);
                    localStorage.setItem(
                        constants.LocalStorageKeys.PROVIDER_LICENSES,
                        JSON.stringify(data?.licenses || []),
                    );
                    const performerOrganizations =
                        data.performerOrganizations?.filter((org) => org.type === OrganizationTypes.Clinic) || [];

                    if (performerOrganizations.length) {
                        const selectedOrg =
                            performerOrganizations.find((organization) => organization.name === data.organization) ??
                            data.performerOrganizations[0];

                        this.selectedOrganization.next(selectedOrg);
                    }
                }),
                switchMap(() => this.providerSubject.asObservable()),
            );
        }

        return this.providerSubject.asObservable();
    }

    getProviderById(id: string): Observable<Provider> {
        return this.network.fetchResource<Provider>(`${this.apiUrl}/${id}`);
    }

    public createProvider(provider): Observable<ProviderUpdatedResponse> {
        const link = '/api/performers';

        return this.network.postResource<ProviderUpdatedResponse>(link, provider);
    }

    public updateProvider(provider): Observable<ProviderUpdatedResponse> {
        const link = `/api/performers/profile/${provider.id}`;

        delete provider.id;
        delete provider.links;

        return this.network.putResource(link, provider);
    }

    public deleteProvider(provider): Observable<any> {
        const link = `/api/performers/${provider.id}`;

        return this.network.deleteResource(link);
    }

    public deletePerformerById(performerId: string): Observable<any> {
        const link = `/api/performers/${performerId}`;

        return this.network.deleteResource(link);
    }

    private getProviderByLink(link: string): Observable<Provider> {
        return this.network.fetchResource(link).pipe(
            map((iProvider: any) => {
                iProvider.specialtiesObj =
                    iProvider.specialties && iProvider.specialties.length ? iProvider.specialties : [];

                return iProvider;
            }),
            mergeMap((iProvider: any) => {
                let url = this.linkUtil.parseLink(iProvider.links, 'providerImage');

                if (url === '') {
                    url = this.linkUtil.parseLink(iProvider.links, 'image');
                }

                return this.imageService.getImageNew(url, 300).pipe(
                    map((profileImage) => ({ ...iProvider, profileImage })),
                    catchError(() => of({ ...iProvider, profileImage: '' })),
                );
            }),
        );
    }

    public clearProvider() {
        this.providerSubject.next(undefined);
    }

    isOwner(createdByAccountId?: string): Observable<boolean> {
        return new Observable<UserSession>((observer: Observer<any>) => {
            if (this.userSession.sessionAccount) {
                observer.next(this.userSession.sessionAccount);
            } else {
                observer.next(null);
            }
        }).pipe(
            map(
                (sessionAccount: UserSession) =>
                    sessionAccount.accountId && sessionAccount.accountId === createdByAccountId,
            ),
        );
    }

    public getOrganizationTypes() {
        const link = '/api/organizations/all';

        return this.network.fetchResource(link);
    }

    public getAppointmentsForMonth(performerId, date, isSuperAdmin: boolean): Observable<Appointment[]> {
        const { startOfMonth, endOfMonth } = DateUtility.getMonthByDate(date);
        const organizationId = localStorage.getItem(ORGANIZATION_ID);
        const link =
            isSuperAdmin && organizationId
                ? `/api/performers/organization/${organizationId}/appointments?dateFrom=${startOfMonth}&dateTo=${endOfMonth}`
                : `/api/performers/${performerId}/appointments?dateFrom=${startOfMonth}&dateTo=${endOfMonth}`;

        return this.network.fetchResource(link).pipe(
            mergeMap((appointments: Appointment[]) => {
                if (appointments.length) {
                    const tasks = appointments.map((c) => {
                        const patientImgLink = this.linkUtil.parseLink(c.links, 'patientImg');

                        if (patientImgLink) {
                            return this.imageService.getImageNew(patientImgLink).pipe(
                                map((patientImg) => ({ ...c, patientImg })),
                                catchError(() => of(c)),
                            );
                        }

                        return of(c);
                    });

                    return forkJoin(tasks);
                }

                return of([]);
            }),
        );
    }

    public getProviderList(link): Observable<ProviderResponse> {
        return this.network.fetchResource(link);
    }

    public setSelectedOrganization(org: Branch): void {
        this.selectedOrganizationId = org?.id;
        this.selectedOrganization.next(org);
    }

    public getSelectedOrganization(): Observable<Branch> {
        return this.selectedOrganization.asObservable();
    }

    public getAppointments(OrgId, startDate, endDate, status, includeEncounter): Observable<ITaskList> {
        const link = `/api/tasks/${OrgId} /location?dueDateFrom=${startDate}&dueDateTo=${endDate}&status=${status}&includeEncounter=${includeEncounter}`;

        return this.network.fetchResource<ITaskList>(link);
    }

    public getAppointmentAnalytics(clinicId, startDate, endDate): Observable<any> {
        const link = `/api/organizations/${clinicId}/hourly-summary-chart-data`;
        const params = new HttpParams().set('startDate', startDate).set('endDate', endDate);

        return this.network.fetchResource(link, { params });
    }

    public updateProviderOrganization(accountId, clinicId): Observable<any> {
        const link = `/api/accounts/${accountId}`;

        return this.network.putResource(link, { organizationId: clinicId }).pipe(
            tap({
                complete: () => {
                    const currentUrl = this.router.url;

                    this.operationUserSessionService.getUserSession(true).subscribe({
                        complete: () => {
                            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                                this.router.navigateByUrl(currentUrl).then();
                            });
                        },
                    });
                },
            }),
        );
    }

    public getProviderDetails() {
        return this.providerSubject.asObservable();
    }

    public getProvidersByOrgId(orgId): Observable<Provider[]> {
        const link = `/api/performers/${orgId}/providers`;

        return this.network.fetchResource(link);
    }

    public checkInPatient(patientId: string, taskId: string): Observable<CareplanTaskModel> {
        const link = `/api/tasks/${patientId}/Check-In-Patient`;
        let httpParams = null;

        if (taskId) {
            httpParams = new HttpParams().set('taskId', taskId);
        }

        return this.network.fetchResource(link, { params: httpParams });
    }

    public getProvidersFromBranches(orgId: string): Observable<Provider[]> {
        const link = `/api/organizations/${orgId}/providersFromBranches`;

        return this.network.fetchResource(link);
    }

    public getEncounterHistory(patientId: string, taskId?: string, params?): Observable<IEncounterHistoryListResource> {
        const link = `/api/encounters/patientEncounters/${patientId}`;
        let httpParams = new HttpParams();

        if (taskId) {
            httpParams = httpParams.append('taskId', taskId);
        }
        httpParams = httpParams.append('page', params?.page || 0);
        httpParams = httpParams.append('size', params?.size || 10);

        return this.network.fetchResource(link, { params: httpParams });
    }

    public getEncounterHistoryCount(patientId: string): Observable<number> {
        const link = `/api/encounters/patientEncountersCount/${patientId}`;

        return this.network.fetchResource(link);
    }

    public getEncounterClaim(encounterId: string): Observable<any> {
        const link = `/api/encounters/${encounterId}/CMSByEncounter`;

        return this.network.fetchResource(link);
    }

    public getPatientCarePlans(patientId: string): Observable<any> {
        const link = `/api/carePlans/${patientId}/getPatientActiveCareplans`;

        return this.network.fetchResource(link);
    }

    public getPatientCarePlanDetail(carePlanId: string): Observable<any> {
        const link = `/api/carePlans/${carePlanId}`;

        return this.network.fetchResource(link);
    }

    public getEncounterInterventionCodes(patientId: string): Observable<any> {
        const link = `/api/encounters/patientInterventionCodes/${patientId}`;

        return this.network.fetchResource(link);
    }

    public getEncounterDiagnosisCodes(patientId: string): Observable<any> {
        const link = `/api/encounters/patientDiagnosis/${patientId}`;

        return this.network.fetchResource(link);
    }

    public GenerateNotesByTranscription(
        patientId: string,
        encounterId: string,
        servicesAvailable: any,
        transcript: any,
    ): Observable<any> {
        const link = '/api/ambient-dictation';
        const body = {
            patientId,
            encounterId,
            transcript,
            cptCodes: servicesAvailable,
        };

        return this.network.postResource(link, body);
    }

    public getAppointmentsLocation(OrgId, startDate, endDate) {
        const link = `/api/tasks/${OrgId}/location-appointment?dueDateFrom=${startDate}&dueDateTo=${endDate}`;

        return this.network.fetchResource(link);
    }

    public getStudent(studentId: string, schoolIds: string[]): Observable<any> {
        const link = '/api/patients/student-id';
        const body = {
            studentId,
            schoolIds,
        };

        return this.network.postResource(link, body);
    }

    public getPatientGroups(accountId: string): Observable<IPatientGroups> {
        const link = `/api/groups/accounts/${accountId}`;

        return this.network.fetchResource(link);
    }

    public getPatientGroup(groupId: string): Observable<IPatientGroup> {
        const link = `/api/groups/${groupId}`;

        return this.network.fetchResource(link);
    }

    public startGroupEncounter(request: IEncounterRequest): Observable<any> {
        const link = '/api/encounters/startGroupEncounter';

        return this.network.postResource(link, request);
    }

    public createPatientGroup(body: { groupName: string; organizationId: string }): Observable<IPatientGroup> {
        const link = '/api/groups/createGroup';

        return this.network.postResource(link, body);
    }

    public updatePatientGroup(groupId: string, body: { groupName: string }): Observable<IPatientGroup> {
        const link = `/api/groups/${groupId}`;

        return this.network.postResource(link, body);
    }

    public updatePatientList(groupId: string, body: { patientIds: string[] }): Observable<IPatientGroup> {
        const link = `/api/groups/${groupId}/managePatients`;

        return this.network.postResource(link, body);
    }

    getProviderByAccountId(accountId: string): Observable<Provider> {
        const url = this.apiUrl + `/account/${accountId}`;

        return this.network.fetchResource(url);
    }

    public getEncounterHistoryForGroup(groupId: string, param?): Observable<any> {
        const link = `/api/encounters/groups/${groupId}/history`;
        let params = new HttpParams();

        if (param?.page >= 0) params = params.set('page', param.page);
        if (param.size) params = params.set('size', param.size);

        return this.network.fetchResource(link, { params });
    }

    public getEncounterDiagnosisCodesForGroup(groupId: string): Observable<any> {
        const link = `/api/encounters/groups/${groupId}/icdCodes`;

        return this.network.fetchResource(link);
    }

    public getEncounterInterventionCodesForGroup(groupId: string): Observable<any> {
        const link = `/api/encounters/groups/${groupId}/interventionCodes`;

        return this.network.fetchResource(link);
    }

    public deletePatientGroup(groupId: string): Observable<IPatientGroup> {
        const link = `/api/groups/${groupId}`;

        return this.network.deleteResource(link);
    }

    getProviderOrganizations(providerId: string): Observable<Provider> {
        const url = this.apiUrl + `/${providerId}/organizations`;

        return this.network.fetchResource(url);
    }

    public deleteEncounter(encounterId: string): Observable<any> {
        const link = `/api/encounters/${encounterId}/remove`;

        return this.network.deleteResource(link);
    }
}
